import {testIdProps} from '@github-ui/test-id-props'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {Flash, Header, Link} from '@primer/react'
import {memo, useCallback, useLayoutEffect, useRef, useState} from 'react'
import invariant from 'tiny-invariant'

import {SidePanelTypeParam} from '../../api/memex-items/side-panel-item'
import type {SuggestedRepository} from '../../api/repository/contracts'
import {
  ProjectDescriptionAdd,
  ProjectDescriptionSidePanelUI,
  ProjectDescriptionUpdate,
  ProjectReadmeAdd,
  ProjectReadmeSettingsPageUI,
  ProjectReadmeUpdate,
} from '../../api/stats/contracts'
import {assertNever} from '../../helpers/assert-never'
import {resetScrollPositionImmediately} from '../../helpers/scroll-utilities'
import {usePostStats} from '../../hooks/common/use-post-stats'
import type {SidePanelState} from '../../hooks/use-side-panel'
import {useSidePanelDirtyState} from '../../hooks/use-side-panel-dirty-state'
import {isInstanceOfMemexItemModel, type MemexItemModel} from '../../models/memex-item-model'
import {IssueStateProvider} from '../../state-providers/issues/issue-state-provider'
import {useIssueContext} from '../../state-providers/issues/use-issue-context'
import {useProjectDetails} from '../../state-providers/memex/use-project-details'
import {useProjectState} from '../../state-providers/memex/use-project-state'
import {DescriptionEditor} from '../description-editor'
import {SanitizedHtml} from '../dom/sanitized-html'
import type {OmnibarItemAttrs} from '../omnibar/types'
import {ShortDescriptionEditor} from '../short-description-editor'
import {StatusUpdates} from '../status-updates/status-updates'
import {CopilotAssignmentModalProvider} from '../table/copilot-assignment-modal-provider'
import {CopilotAssignmentModalRenderer} from '../table/copilot-assignment-modal-renderer'
import {AgentTaskSidePanelContent} from './agent-task-content'
import {SidePanelBody} from './body'
import {BulkAddItemsProvider} from './bulk-add/bulk-add-items-provider'
import {BulkAddView} from './bulk-add/bulk-add-view'
import {SidePanelComments} from './comments'
import styles from './content.module.css'
import {SidePanelHeader, type SidePanelTabName, SidePanelToolbar} from './header'
import {RecapContent} from './recap-content'
import {SharedIssueViewer} from './shared-issue-viewer'
import {SidePanelSidebar} from './sidebar'

export const SidePanelContent = memo(function SidePanelContent({
  sidePanelState,
}: {
  sidePanelState: NonNullable<SidePanelState>
}) {
  switch (sidePanelState.type) {
    case SidePanelTypeParam.ISSUE: {
      if (sidePanelState.item.contentType === 'Issue') {
        const issueIdentifier = sidePanelState.item.getItemIdentifier()
        // If in standalone dev mode, don't render the new issue viewer
        if (
          (process.env.APP_ENV === 'development' || process.env.APP_ENV === 'staging') &&
          process.env.IS_STANDALONE === 'TRUE'
        ) {
          return <NewIssueViewerDevelopmentWarning />
        }

        // New issue viewer
        return issueIdentifier ? (
          <SharedIssueViewer {...issueIdentifier} url={sidePanelState.item.getUrl()} itemId={sidePanelState.item.id} />
        ) : null
        // Legacy issue viewer used for draft issues
      } else if (sidePanelState.item.contentType === 'DraftIssue') {
        invariant(isInstanceOfMemexItemModel(sidePanelState.item), 'sidePanelState.item is not a MemexItemModel')
        return (
          <IssueStateProvider
            contentType={sidePanelState.item.contentType}
            repositoryId={sidePanelState.item.ownerId()}
            itemId={sidePanelState.item.itemId()}
            memexItemId={sidePanelState.item.memexItemId?.()}
          >
            <SidePanelItemContent item={sidePanelState.item} />
          </IssueStateProvider>
        )
      } else {
        // This should never happen as PullRequest and RedactedItem should never open the issue viewer
        throw new Error(`Unexpected contentType of ${sidePanelState.item.contentType} for SidePanelTypeParam.ISSUE`)
      }
    }
    case SidePanelTypeParam.INFO: {
      return <SidePanelMemexInfoContent />
    }
    case SidePanelTypeParam.BULK_ADD: {
      return (
        <SidePanelMemexBulkAddContent
          targetRepository={sidePanelState.targetRepository}
          query={sidePanelState.query}
          newItemAttributes={sidePanelState.newItemAttributes}
        />
      )
    }
    case SidePanelTypeParam.RECAP: {
      return <RecapContent />
    }
    case SidePanelTypeParam.AGENT_TASK: {
      return (
        <div className={styles.agentTaskPanelContainer} {...testIdProps('side-panel-agent-task-content')}>
          <SidePanelToolbar showCloseButton showPinButton={false} />
          <AgentTaskSidePanelContent taskId={sidePanelState.taskId} repoId={sidePanelState.repoId} />
        </div>
      )
    }
    default: {
      assertNever(sidePanelState)
    }
  }
})

const SidePanelMemexInfoContent = memo(function SidePanelMemexInfoContent() {
  const {postStats} = usePostStats()
  const {titleHtml} = useProjectDetails()
  const [, setHasUnsavedShortDescription] = useSidePanelDirtyState()
  const [, setHasUnsavedDescription] = useSidePanelDirtyState()
  const {isTemplate} = useProjectState()

  const onSaveStatsShortDescription = useCallback(
    (update: boolean) => {
      postStats({
        name: update ? ProjectDescriptionUpdate : ProjectDescriptionAdd,
        ui: ProjectDescriptionSidePanelUI,
      })
    },
    [postStats],
  )

  const onSaveStatsDescription = useCallback(
    (update: boolean) => {
      postStats({
        name: update ? ProjectReadmeUpdate : ProjectReadmeAdd,
        ui: ProjectReadmeSettingsPageUI,
      })
    },
    [postStats],
  )

  return (
    <div className={styles.infoPanelContainer} {...testIdProps('side-panel-info-content')}>
      <div className={styles.infoPanelContent}>
        <div className={styles.infoPanelHeader}>
          <SidePanelToolbar showCloseButton />
          <div className={styles.headerSpacer} />
          <SanitizedHtml as="h2" className={styles.infoPanelTitle}>
            {titleHtml}
          </SanitizedHtml>
        </div>

        <ShortDescriptionEditor
          onSaveStats={onSaveStatsShortDescription}
          setHasUnsavedChanges={setHasUnsavedShortDescription}
          hideLabel
        />
      </div>
      <div className={styles.descriptionEditorContainer}>
        <DescriptionEditor
          onSaveStats={onSaveStatsDescription}
          setHasUnsavedChanges={setHasUnsavedDescription}
          hideLabel
        />
      </div>
      {!isTemplate && <StatusUpdates />}
    </div>
  )
})

// Could just get supportsComments and isLoading from the useIssueContext hook, but this helps minimize hook use
// to minimize re-renders
const SidePanelItemDetails = memo<{item: MemexItemModel; isLoading: boolean}>(function SidePanelItemDetails({
  item,
  isLoading,
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const itemId = item.itemId()

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      resetScrollPositionImmediately(scrollContainerRef.current)
    }
  }, [itemId])

  // eslint-disable-next-line react-hooks/refs
  const breakpoint = useContainerBreakpoint(scrollContainerRef.current)

  return (
    <div
      ref={scrollContainerRef}
      style={{
        flexDirection: breakpoint(['column', 'column', 'row', 'row']),
      }}
      className={styles.itemDetailsContainer}
    >
      <div
        style={{
          width: breakpoint(['100%', '100%', '66%', '66%']),
        }}
        className={styles.itemMainContent}
      >
        <SidePanelBody item={item} isLoading={isLoading} />
        <SidePanelComments itemURL={item.getUrl()} />
      </div>
      <SidePanelSidebar item={item} breakpoint={breakpoint} />
    </div>
  )
})

const SidePanelItemContent = memo<{
  item: MemexItemModel
}>(function SidePanelItemContent({item}) {
  const [selectedTab, setSelectedTab] = useState<SidePanelTabName>('details')
  const {isLoading} = useIssueContext()
  const showTabs = false // Tabs are permanently disabled pending future hierarchy works

  return (
    <div className={styles.developmentWarningContainer}>
      <SidePanelHeader
        item={item}
        isLoading={isLoading}
        showTabs={showTabs}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
      />
      <SidePanelItemDetails item={item} isLoading={isLoading} />
    </div>
  )
})

const SidePanelMemexBulkAddContent = memo<{
  targetRepository?: SuggestedRepository
  query?: string
  newItemAttributes?: OmnibarItemAttrs
}>(function SidePanelMemexBulkAddContent({targetRepository, query, newItemAttributes}) {
  return (
    <CopilotAssignmentModalProvider>
      <BulkAddItemsProvider query={query} selectedRepository={targetRepository}>
        <BulkAddView targetRepository={targetRepository} newItemAttributes={newItemAttributes} />
        <CopilotAssignmentModalRenderer />
      </BulkAddItemsProvider>
    </CopilotAssignmentModalProvider>
  )
})

const NewIssueViewerDevelopmentWarning = memo(function NewIssueViewerDevelopmentWarning() {
  return (
    <div {...testIdProps('new-issue-viewer-development-warning')} className={styles.developmentWarningContainer}>
      <SidePanelToolbar showCloseButton />
      <Header.Item className={styles.developmentWarningHeader}>
        <Flash variant="warning" className={styles.developmentWarningFlash}>
          <p>
            We decided not to render the new issue viewer in standalone development environment.{' '}
            <Link
              href="https://github.com/github/memex/blob/main/docs/adr/2024-05-10-issue-viewer-testing.md"
              target="_blank"
              inline
            >
              Learn more
            </Link>
            .
          </p>
          <p>
            E2E tests for the new issue viewer should be added to{' '}
            <Link
              href="https://github.com/github/github/blob/master/test/e2e/spec/hyperlist-web/memex-issue-viewer.spec.ts"
              target="_blank"
            >
              <code>/test/e2e/spec/hyperlist-web/memex-issue-viewer.spec.ts</code>
            </Link>
            .{' '}
          </p>
        </Flash>
      </Header.Item>
    </div>
  )
})
